/* Verbatim port of exportSameAs (service/review/index.html ~3385-3397) and
 * exportQuickStatements (~3398-3412) — the two §2 Entity export buttons'
 * onclick targets. Their text (`onclick="exportSameAs()"` /
 * `onclick="exportQuickStatements()"`) lives inside views/entity.ts's
 * byte-identical innerHTML template literal, so — same treatment as
 * ui/drawer.ts's closeDrawer — main.ts wires them onto `window` rather
 * than views/entity.ts importing and calling them directly.
 *
 * PARAMETERIZED (rule a): both read the global `STATE` (`STATE.brand`,
 * `STATE.assets`, `STATE.wikidata`, and `coreAsset()`, itself a STATE
 * reader) — each takes `state: AppPayloadState` as an explicit first
 * parameter.
 *
 * RESOLVED (F2d): `${WS.ICON.down}` prefixes both buttons' label in the
 * original (~2334, ~2398, ~2408), unconditionally — unlike every `WS.*`
 * reference views/exec.ts ported (wrapped in `typeof WS !== 'undefined' &&
 * WS.available === true`), since in the original WS (~3655) is a real
 * `const` whose static `ICON` dictionary (~3870) exists regardless of the
 * shared board's live status. Until F2d, this hub's WS wasn't a real
 * runtime value yet (only the ambient `any` shim in the now-deleted
 * hub/src/types/legacy-globals.d.ts), so views/entity.ts (this function's
 * only caller-adjacent template) carried a byte-identical stand-in
 * constant, `ICON_DOWN`, instead of the live reference — porting
 * `${WS.ICON.down}` verbatim would have thrown `ReferenceError: WS is not
 * defined` on every renderEntity() call (not a dead/unreachable branch
 * like exec.ts's WS references — this template always executes). F2d
 * lands the real WS module (state/workspace.ts): views/entity.ts now
 * reads `${WS.ICON.down}` directly (the same bare-global pattern exec.ts/
 * conflicts.ts use) and this file's `ICON_DOWN` stand-in is deleted.
 *
 * F2d: exportDataBundle (old ~3379-3422, `onclick="exportDataBundle()"` via
 * `document.getElementById('btnExport').onclick`, old ~3504) joins this
 * file for the same reason as the other two — its only caller is a
 * window-shim binding (hub/src/main.ts), not a view module. Same
 * PARAMETERIZED treatment: `state: AppPayloadState` replaces every `STATE`
 * read (direct, and transitively via footprint/searchHeadline/aiHeadline/
 * entityHeadline/planeVerdicts). ACT_STATUS (via `actionPlan()`, no-arg in
 * the original) is sourced from `actStatusStore.get()` rather than taken as
 * an explicit parameter — same as views/actions.ts's exportActionsCsv,
 * which faces the identical STATE+ACT_STATUS pair and resolves it the same
 * way (this module sits at the same UI-layer altitude as that view, one
 * step below the pure derive/* functions that DO take actStatus explicitly).
 */
import { domainOf, slugify } from '../lib/format.ts';
import { canEdit } from '../derive/axes.ts';
import { coreAsset, footprint } from '../derive/exec.ts';
import { entityHeadline, planeVerdicts } from '../derive/entity.ts';
import { searchHeadline } from '../derive/search.ts';
import { aiHeadline } from '../derive/ai.ts';
import { actionPlan } from '../derive/actions.ts';
import { actStatusStore } from '../state/ui.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

/* The full research + analysis bundle: everything an AM needs to hand to
   Claude (or any LLM) to draft decks, work orders, or client emails without
   this hub. One canonical computation layer feeds it, so the numbers in the
   export match the numbers on screen by construction. */
export function exportDataBundle(state: AppPayloadState): void {
  const strip = (s: unknown) => String(s ?? '').replace(/<[^>]+>/g, '');
  const verdicts = planeVerdicts(state).map((v) => ({
    plane: v.plane, status: v.st === 'win' ? 'in good shape' : v.st === 'hold' ? 'needs work' : 'exposed',
    headlineNumber: strip(v.num), numberMeaning: v.numLabel, reading: strip(v.read) }));
  const bundle = {
    format: 'fiveblocks-digital-presence-audit/v1',
    brand: state.brand,
    exportedAt: new Date().toISOString(),
    instructionsForAI: [
      `This is a Five Blocks digital-presence audit for "${state.brand}": a complete map of where the brand exists online, cross-referenced with live Google search data (IMPACT), AI-engine citation data (AIQ), and a deterministic link crawl.`,
      'How to read the data — the firm\'s vocabulary is non-negotiable:',
      '- assets[]: every surface found. category: owned = client owns it outright; editable = client has direct edit access (their social profiles, claimed listings); claimable = could be claimed but hasn\'t been; media = cannot compliantly edit (press, Wikipedia, forums). Wikipedia is ALWAYS media. sentiment values are research judgments unless backed by fields.impactMeanPosition / fields.aiCitations30d (measured).',
      '- assets[].pageFacts: what a deterministic crawler observed on that URL (HTTP status, links to other mapped assets, sameAs markup, how it was fetched). fetched:false with reason "platform blocks bots" means unverifiable, NOT absent.',
      '- edges[]: relationships between assets. observed:true means a crawler/markup/Wikidata actually proved the link; observed:false means the research agent asserted it. Never present asserted links as facts.',
      '- linkGraph.bridges[]: the ranked missing cross-links (each with concrete fixes) — the entity-cohesion worklist. linkGraph.orphans[]: client properties nothing observed points at.',
      '- aiNarrative.sourceOwnership = share of AI citations from client-controlled sources (own sites + profiles it runs). peers[].selfCitationShare = OWN-DOMAIN citations only. These are different metrics — only compare peers on selfCitationShare.',
      '- serp/impact: measured Google data per term × location. Sentiment axis words: Desirable / Undesirable / Neutral. Ownership axis: Owned / Not Owned / Not Me.',
      '- Wikipedia work is COI-compliant only: transparent Talk-page edit requests, never direct editing. Never recommend soliciting only positive reviews (review-gating violates platform policy).',
      'summary/ contains the pre-computed analysis (verdicts, headlines, ranked action plan with the AM\'s live statuses). Trust these numbers — they are the canonical computations from the hub.',
      'Good things to produce from this: a state-of-search deck (exec summary → search per city → Knowledge Panel/entity → AI narrative → per-platform presence → Wikipedia → action plan), per-platform work orders, a client kickoff email, or a prioritized 90-day program plan.'
    ].join('\n'),
    summary: {
      footprint: footprint(state),
      search: searchHeadline(state),
      aiNarrative: aiHeadline(state),
      entity: entityHeadline(state),
      verdicts,
      actionPlan: actionPlan(state, actStatusStore.get()),
    },
    data: {
      entity: state.entity, knowledgePanel: state.knowledgePanel, wikidata: state.wikidata,
      assets: state.assets, edges: state.edges, conflicts: state.conflicts,
      opportunities: state.opportunities, coverageSummary: state.coverage,
      linkGraph: state.linkGraph, serp: state.serp, impact: state.impact,
      aiNarrative: state.aiNarrative, peers: state.peers,
    },
    dossier: state.dossier || null,
  };
  const blob = new Blob([JSON.stringify(bundle, null, 1)], {type:'application/json'});
  const url = URL.createObjectURL(blob); const a = document.createElement('a');
  a.href = url; a.download = `${slugify(state.brand)}-audit-${new Date().toISOString().slice(0,10)}.json`;
  a.click(); URL.revokeObjectURL(url);
}

export function exportSameAs(state: AppPayloadState): void {
  const core = coreAsset(state);
  const profiles = state.assets.filter((a) =>
    (a.category === 'owned' || canEdit(a)) && a.url &&
    (a.assetType === 'social-profile' || a.assetType === 'video-channel' || a.role === 'hub' || a.assetType === 'website') &&
    !a.isCoreEntity);
  const seen = new Set<string>(); const urls: string[] = [];
  // (a.url || '') restates the filter's a.url check for TS — same values reach here
  profiles.forEach((a) => { const u = (a.url || '').replace(/#.*$/,''); if (!seen.has(u)){ seen.add(u); urls.push(u); } });
  const jsonld = { "@context":"https://schema.org", "@type":"Organization", "name": state.brand, "url": core && core.url, "sameAs": urls };
  const text = `<!-- Add to ${core?domainOf(core.url):'the site'} <head>. Confirms these are ${state.brand}'s official profiles for Google's entity reconciliation. -->\n<script type="application/ld+json">\n${JSON.stringify(jsonld,null,2)}\n<\/script>`;
  const blob = new Blob([text],{type:'text/plain'}); const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `${slugify(state.brand)}-sameas.html`; a.click(); URL.revokeObjectURL(url);
}
export function exportQuickStatements(state: AppPayloadState): void {
  const wd = state.wikidata; if (!wd) return;
  const qid = wd.qid || 'LAST';
  const lines: string[] = [];
  (wd.worksheet||[]).filter((w) => w.state === 'missing' || w.state === 'stale').forEach((w) => {
    const val = (w.research||'').replace(/\t/g,' ').trim();
    if (!w.pcode || !val) return;
    let stmt = `${qid}\t${w.pcode}\t"${val}"`;
    if (w.source) stmt += `\tS854\t"${w.source}"`;
    lines.push(`# ${w.property} (${w.state})`); lines.push(stmt);
  });
  const blob = new Blob([lines.join('\n') || '# no missing/stale claims to add'], {type:'text/plain'});
  const url = URL.createObjectURL(blob); const a = document.createElement('a');
  a.href = url; a.download = `${slugify(state.brand)}-quickstatements.txt`; a.click(); URL.revokeObjectURL(url);
}
